import { icon, IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faBorderAll,
  faBorderBottom,
  faBorderCenterH,
  faBorderCenterV,
  faBorderInner,
  faBorderLeft,
  faBorderNone,
  faBorderOuter,
  faBorderRight,
  faBorderTop,
} from '@fortawesome/pro-light-svg-icons';
import { faBorderAll as farBorderAll } from '@fortawesome/pro-regular-svg-icons';
import FroalaEditor from 'froala-editor';
import i18n from '../../../i18n';

const BORDER_VALUE = '1px solid #DDD';

const getIcon = (iconDefinition: IconDefinition) => {
  return [
    icon(iconDefinition).html[0].slice(0, 4),
    " style='margin-bottom: 4px'",
    icon(iconDefinition).html[0].slice(4),
  ].join('');
};

const TABLE_CELL_BUTTONS = [
  'borderBottom',
  'borderTop',
  'borderLeft',
  'borderRight',
  'borderNone',
  'borderAll',
  'borderOuter',
  'borderInner',
  'horizontalBorderInner',
  'verticalBorderInner',
] as const;

const TABLE_CELL_RECORD: Record<
  (typeof TABLE_CELL_BUTTONS)[number],
  {
    title: string;
    iconHtml: string;
  }
> = {
  borderBottom: {
    title: i18n.t('common:froala.tableCellBorders.type.borderBottom'),
    iconHtml: getIcon(faBorderBottom),
  },
  borderTop: {
    title: i18n.t('common:froala.tableCellBorders.type.borderTop'),
    iconHtml: getIcon(faBorderTop),
  },
  borderLeft: {
    title: i18n.t('common:froala.tableCellBorders.type.borderLeft'),
    iconHtml: getIcon(faBorderLeft),
  },
  borderRight: {
    title: i18n.t('common:froala.tableCellBorders.type.borderRight'),
    iconHtml: getIcon(faBorderRight),
  },
  borderNone: {
    title: i18n.t('common:froala.tableCellBorders.type.borderNone'),
    iconHtml: getIcon(faBorderNone),
  },
  borderAll: {
    title: i18n.t('common:froala.tableCellBorders.type.borderAll'),
    iconHtml: getIcon(faBorderAll),
  },
  borderOuter: {
    title: i18n.t('common:froala.tableCellBorders.type.borderOuter'),
    iconHtml: getIcon(faBorderOuter),
  },
  borderInner: {
    title: i18n.t('common:froala.tableCellBorders.type.borderInner'),
    iconHtml: getIcon(faBorderInner),
  },
  horizontalBorderInner: {
    title: i18n.t('common:froala.tableCellBorders.type.horizontalBorderInner'),
    iconHtml: getIcon(faBorderCenterH),
  },
  verticalBorderInner: {
    title: i18n.t('common:froala.tableCellBorders.type.verticalBorderInner'),
    iconHtml: getIcon(faBorderCenterV),
  },
};

FroalaEditor.DefineIconTemplate('fontAwesome', '[HTML]');
// Define an icon and command for the button that opens the custom popup.
FroalaEditor.DefineIcon('tableCellBorders', {
  NAME: 'borderAll',
  template: 'fontAwesome',
  HTML: getIcon(farBorderAll),
});
FroalaEditor.RegisterCommand('tableCellBorders', {
  title: i18n.t('common:froala.tableCellBorders.button'),
  icon: 'tableCellBorders',
  type: 'dropdown',
  undo: false,
  focus: false,
  options: {
    option1: '<i class="fa fa-star"></i> Option 1',
    borderBottom: 'Border Bottom',
    borderTop: 'Border Top',
    borderLeft: 'Border Left',
    borderRight: 'Border Right',
    borderNone: 'Border None',
    borderAll: 'Border All',
    borderOuter: 'Border Outer',
    borderInner: 'Border Inner',
    horizontalBorderInner: 'Horizontal Border Inner',
    verticalBorderInner: 'Vertical Border Inner',
  },
  html: function () {
    return `<ul class="fr-dropdown-list" role="presentation">${TABLE_CELL_BUTTONS.map(
      (button) => {
        const isListItemWithDivider =
          button === 'borderRight' || button === 'borderInner';

        return `<li role="presentation"${isListItemWithDivider ? ' style="border-bottom:1px solid var(--prioCl-color-application-border)"' : ''}><a class="fr-command" tabindex="-1" role="option" data-cmd="tableCellBorders" data-param1="${button}" title="${TABLE_CELL_RECORD[button].title}" aria-selected="false">${TABLE_CELL_RECORD[button].iconHtml}<span style="margin-left:16px;">${TABLE_CELL_RECORD[button].title}</span></a></li>`;
      }
    ).join('')}</ul>`;
  },
  callback: function (
    cmd: 'tableCellBorders',
    val: (typeof TABLE_CELL_BUTTONS)[number]
  ) {
    const editor: FroalaEditor = this as FroalaEditor;
    const selectedCells: NodeList =
      editor.table.selectedCells() as any as NodeList;

    if (selectedCells.length > 0) {
      const table = selectedCells[0]?.parentElement?.parentElement;
      const rowsLength = (table as HTMLTableElement)?.rows.length;
      const columnsLength = (table as HTMLTableElement)?.rows[0]?.cells.length;

      const firstSelectedRowIndex = (
        selectedCells[0]?.parentElement as HTMLTableRowElement
      )?.rowIndex;
      const lastSelectedRowIndex = (
        selectedCells[selectedCells.length - 1]
          ?.parentElement as HTMLTableRowElement
      )?.rowIndex;
      const firstSelectedColumnIndex = (
        selectedCells[0] as HTMLTableCellElement
      ).cellIndex;
      const lastSelectedColumnIndex = (
        selectedCells[selectedCells.length - 1] as HTMLTableCellElement
      ).cellIndex;

      const selectedCellsFromFirstRow = Array.from(selectedCells).filter(
        (cell) => {
          const cellRowIndex = (cell.parentElement as HTMLTableRowElement)
            ?.rowIndex;
          return cellRowIndex === firstSelectedRowIndex;
        }
      ) as HTMLTableCellElement[];
      const selectedCellsAboveFirstRow =
        firstSelectedRowIndex === 0
          ? []
          : (Array.from(
              (
                (selectedCells[0]?.parentElement as HTMLTableRowElement)
                  ?.parentElement as HTMLTableElement
              )?.rows[firstSelectedRowIndex - 1]?.cells ?? []
            ).filter((cell) => {
              const cellIndex = (cell as HTMLTableCellElement)?.cellIndex;
              return (
                cellIndex >= firstSelectedColumnIndex &&
                cellIndex <= lastSelectedColumnIndex
              );
            }) as HTMLTableCellElement[]);

      const selectedCellsFromFirstColumn = Array.from(selectedCells).filter(
        (cell) => {
          const cellColumnIndex = (cell as HTMLTableCellElement).cellIndex;
          return cellColumnIndex === firstSelectedColumnIndex;
        }
      ) as HTMLTableCellElement[];
      const selectedCellsBeforeFirstColumn =
        firstSelectedColumnIndex === 0
          ? []
          : (Array.from(
              Array.from(
                (
                  (selectedCells[0]?.parentElement as HTMLTableRowElement)
                    ?.parentElement as HTMLTableElement
                )?.rows ?? []
              )?.map((row) => row.cells[firstSelectedColumnIndex - 1])
              .filter(Boolean)
            ).filter((cell) => {
              const cellRowIndex = (cell?.parentElement as HTMLTableRowElement)
                ?.rowIndex;
              return (
                cellRowIndex >= firstSelectedRowIndex &&
                cellRowIndex <= lastSelectedRowIndex
              );
            }) as HTMLTableCellElement[]);

      const selectedCellsFromLastRow = Array.from(selectedCells).filter(
        (cell) => {
          const cellRowIndex = (cell.parentElement as HTMLTableRowElement)
            ?.rowIndex;
          return cellRowIndex === lastSelectedRowIndex;
        }
      ) as HTMLTableCellElement[];
      const selectedCellsBelowLastRow =
        lastSelectedRowIndex === rowsLength - 1
          ? []
          : (
              Array.from(
                (
                  (selectedCells[0]?.parentElement as HTMLTableRowElement)
                    ?.parentElement as HTMLTableElement
                )?.rows[lastSelectedRowIndex + 1]?.cells ?? []
              ).filter((cell) => {
                const cellIndex = (cell as HTMLTableCellElement)?.cellIndex;
                return (
                  cellIndex >= firstSelectedColumnIndex &&
                  cellIndex <= lastSelectedColumnIndex
                );
              }) as HTMLTableCellElement[]
            ).filter((cell) => {
              const cellRowIndex = (cell?.parentElement as HTMLTableRowElement)
                ?.rowIndex;
              return cellRowIndex >= firstSelectedRowIndex;
            });

      const selectedCellsFromLastColumn = Array.from(selectedCells).filter(
        (cell) => {
          const cellColumnIndex = (cell as HTMLTableCellElement).cellIndex;
          return cellColumnIndex === lastSelectedColumnIndex;
        }
      ) as HTMLTableCellElement[];
      const selectedCellsAfterLastColumn =
        lastSelectedColumnIndex === columnsLength - 1
          ? []
          : (
              Array.from(
                Array.from(
                  (
                    (selectedCells[0]?.parentElement as HTMLTableRowElement)
                      ?.parentElement as HTMLTableElement
                  )?.rows ?? []
                )?.map((row) => row.cells[lastSelectedColumnIndex + 1])
                .filter(Boolean)
              ).filter((cell) => {
                const cellRowIndex = (cell?.parentElement as HTMLTableRowElement)
                  ?.rowIndex;
                return (
                  cellRowIndex >= firstSelectedRowIndex &&
                  cellRowIndex <= lastSelectedRowIndex
                );
              }) as HTMLTableCellElement[]
            ).filter((cell) => {
              const cellColumnIndex = (cell as HTMLTableCellElement)?.cellIndex;
              return cellColumnIndex >= firstSelectedColumnIndex;
            });

      switch (val) {
        case 'borderBottom': {
          selectedCellsFromLastRow.forEach((cell) => {
            cell = applyInitialBorders(cell);
            cell = checkAndApplyBorderValue(cell, 'borderBottom');
          });
          selectedCellsBelowLastRow.forEach((cell) => {
            cell = applyInitialBorders(cell);
            cell = checkAndApplyBorderValue(cell, 'borderTop');
          });
          break;
        }
        case 'borderTop': {
          selectedCellsFromFirstRow.forEach((cell) => {
            cell = applyInitialBorders(cell);
            cell = checkAndApplyBorderValue(cell, 'borderTop');
          });
          selectedCellsAboveFirstRow.forEach((cell) => {
            cell = applyInitialBorders(cell);
            cell = checkAndApplyBorderValue(cell, 'borderBottom');
          });
          break;
        }
        case 'borderLeft': {
          selectedCellsFromFirstColumn.forEach((cell) => {
            cell = applyInitialBorders(cell);
            const alreadyHasBorderLeft = cell.style.borderLeft !== 'none';
            if (alreadyHasBorderLeft) {
              cell.style.borderLeft = 'none';
            } else {
              cell.style.borderLeft = BORDER_VALUE;
            }
          });
          selectedCellsBeforeFirstColumn.forEach((cell) => {
            cell = applyInitialBorders(cell);
            const alreadyHasBorderRight = cell.style.borderRight !== 'none';
            if (alreadyHasBorderRight) {
              cell.style.borderRight = 'none';
            } else {
              cell.style.borderRight = BORDER_VALUE;
            }
          });
          break;
        }
        case 'borderRight': {
          selectedCellsFromLastColumn.forEach((cell) => {
            cell = applyInitialBorders(cell);
            cell = checkAndApplyBorderValue(cell, 'borderRight');
          });
          selectedCellsAfterLastColumn.forEach((cell) => {
            cell = applyInitialBorders(cell);
            cell = checkAndApplyBorderValue(cell, 'borderLeft');
          });
          break;
        }
        case 'borderNone': {
          selectedCells.forEach((cell: HTMLTableCellElement) => {
            cell = applyInitialBorders(cell);
            cell.style.borderTop = 'none';
            cell.style.borderBottom = 'none';
            cell.style.borderLeft = 'none';
            cell.style.borderRight = 'none';
          });
          selectedCellsAboveFirstRow.forEach((cell) => {
            cell = applyInitialBorders(cell);
            cell.style.borderBottom = 'none';
          });
          selectedCellsBelowLastRow.forEach((cell) => {
            cell = applyInitialBorders(cell);
            cell.style.borderTop = 'none';
          });
          selectedCellsBeforeFirstColumn.forEach((cell) => {
            cell = applyInitialBorders(cell);
            cell.style.borderRight = 'none';
          });
          selectedCellsAfterLastColumn.forEach((cell) => {
            cell = applyInitialBorders(cell);
            cell.style.borderLeft = 'none';
          });
          break;
        }
        case 'borderAll': {
          selectedCells.forEach((cell: HTMLTableCellElement) => {
            cell = applyInitialBorders(cell);
            cell = checkAndApplyBorderValue(cell, 'borderBottom');
            cell = checkAndApplyBorderValue(cell, 'borderTop');
            cell = checkAndApplyBorderValue(cell, 'borderLeft');
            cell = checkAndApplyBorderValue(cell, 'borderRight');
          });
          selectedCellsAboveFirstRow.forEach((cell) => {
            cell = applyInitialBorders(cell);
            cell = checkAndApplyBorderValue(cell, 'borderBottom');
          });
          selectedCellsBelowLastRow.forEach((cell) => {
            cell = applyInitialBorders(cell);
            cell = checkAndApplyBorderValue(cell, 'borderTop');
          });
          selectedCellsBeforeFirstColumn.forEach((cell) => {
            cell = applyInitialBorders(cell);
            cell = checkAndApplyBorderValue(cell, 'borderRight');
          });
          selectedCellsAfterLastColumn.forEach((cell) => {
            cell = applyInitialBorders(cell);
            cell = checkAndApplyBorderValue(cell, 'borderLeft');
          });
          break;
        }
        case 'borderOuter': {
          selectedCells.forEach((cell: HTMLTableCellElement) => {
            cell = applyInitialBorders(cell);
            if (cell.cellIndex === firstSelectedColumnIndex) {
              cell = checkAndApplyBorderValue(cell, 'borderLeft');
            }
            if (cell.cellIndex === lastSelectedColumnIndex) {
              cell = checkAndApplyBorderValue(cell, 'borderRight');
            }
            if (
              (cell.parentElement as HTMLTableRowElement)?.rowIndex ===
              firstSelectedRowIndex
            ) {
              cell = checkAndApplyBorderValue(cell, 'borderTop');
            }
            if (
              (cell.parentElement as HTMLTableRowElement)?.rowIndex ===
              lastSelectedRowIndex
            ) {
              cell = checkAndApplyBorderValue(cell, 'borderBottom');
            }
          });
          selectedCellsAboveFirstRow.forEach((cell) => {
            cell = applyInitialBorders(cell);
            cell = checkAndApplyBorderValue(cell, 'borderBottom');
          });
          selectedCellsBelowLastRow.forEach((cell) => {
            cell = applyInitialBorders(cell);
            cell = checkAndApplyBorderValue(cell, 'borderTop');
          });
          selectedCellsBeforeFirstColumn.forEach((cell) => {
            cell = applyInitialBorders(cell);
            cell = checkAndApplyBorderValue(cell, 'borderRight');
          });
          selectedCellsAfterLastColumn.forEach((cell) => {
            cell = applyInitialBorders(cell);
            cell = checkAndApplyBorderValue(cell, 'borderLeft');
          });
          break;
        }
        case 'borderInner': {
          selectedCells.forEach((cell: HTMLTableCellElement) => {
            cell = applyInitialBorders(cell);
            if (cell.cellIndex !== firstSelectedColumnIndex) {
              cell = checkAndApplyBorderValue(cell, 'borderLeft');
            }
            if (cell.cellIndex !== lastSelectedColumnIndex) {
              cell = checkAndApplyBorderValue(cell, 'borderRight');
            }
            if (
              (cell.parentElement as HTMLTableRowElement)?.rowIndex !==
              firstSelectedRowIndex
            ) {
              cell = checkAndApplyBorderValue(cell, 'borderTop');
            }
            if (
              (cell.parentElement as HTMLTableRowElement)?.rowIndex !==
              lastSelectedRowIndex
            ) {
              cell = checkAndApplyBorderValue(cell, 'borderBottom');
            }
          });
          selectedCellsAboveFirstRow.forEach((cell) => {
            cell = applyInitialBorders(cell);
            cell = checkAndApplyBorderValue(cell, 'borderBottom');
          });
          selectedCellsBelowLastRow.forEach((cell) => {
            cell = applyInitialBorders(cell);
            cell = checkAndApplyBorderValue(cell, 'borderTop');
          });
          selectedCellsBeforeFirstColumn.forEach((cell) => {
            cell = applyInitialBorders(cell);
            cell = checkAndApplyBorderValue(cell, 'borderRight');
          });
          selectedCellsAfterLastColumn.forEach((cell) => {
            cell = applyInitialBorders(cell);
            cell = checkAndApplyBorderValue(cell, 'borderLeft');
          });
          break;
        }
        case 'horizontalBorderInner': {
          selectedCells.forEach((cell: HTMLTableCellElement) => {
            cell = applyInitialBorders(cell);
            if (
              (cell.parentElement as HTMLTableRowElement)?.rowIndex !==
              firstSelectedRowIndex
            ) {
              cell = checkAndApplyBorderValue(cell, 'borderTop');
            }
            if (
              (cell.parentElement as HTMLTableRowElement)?.rowIndex !==
              lastSelectedRowIndex
            ) {
              cell = checkAndApplyBorderValue(cell, 'borderBottom');
            }
          });
          selectedCellsAboveFirstRow.forEach((cell) => {
            cell = applyInitialBorders(cell);
            cell = checkAndApplyBorderValue(cell, 'borderBottom');
          });
          selectedCellsBelowLastRow.forEach((cell) => {
            cell = applyInitialBorders(cell);
            cell = checkAndApplyBorderValue(cell, 'borderTop');
          });
          break;
        }
        case 'verticalBorderInner': {
          selectedCells.forEach((cell: HTMLTableCellElement) => {
            cell = applyInitialBorders(cell);
            if (cell.cellIndex !== firstSelectedColumnIndex) {
              cell = checkAndApplyBorderValue(cell, 'borderLeft');
            }
            if (cell.cellIndex !== lastSelectedColumnIndex) {
              cell = checkAndApplyBorderValue(cell, 'borderRight');
            }
          });
          selectedCellsBeforeFirstColumn.forEach((cell) => {
            cell = applyInitialBorders(cell);
            cell = checkAndApplyBorderValue(cell, 'borderRight');
          });
          selectedCellsAfterLastColumn.forEach((cell) => {
            cell = applyInitialBorders(cell);
            cell = checkAndApplyBorderValue(cell, 'borderLeft');
          });
          break;
        }
      }

      editor.save.save();
      editor.events.trigger('contentChanged', undefined);
    }
  },
});

const applyInitialBorders = (cell: HTMLTableCellElement) => {
  if (!cell.style.border) {
    if (!cell.style.borderTop) {
      cell.style.borderTop = BORDER_VALUE;
    }
    if (!cell.style.borderBottom) {
      cell.style.borderBottom = BORDER_VALUE;
    }
    if (!cell.style.borderLeft) {
      cell.style.borderLeft = BORDER_VALUE;
    }
    if (!cell.style.borderRight) {
      cell.style.borderRight = BORDER_VALUE;
    }
  } else if (cell.style.border === 'none') {
    cell.style.border = undefined;
    if (!cell.style.borderTop) {
      cell.style.borderTop = BORDER_VALUE;
    }
    if (!cell.style.borderBottom) {
      cell.style.borderBottom = BORDER_VALUE;
    }
    if (!cell.style.borderLeft) {
      cell.style.borderLeft = BORDER_VALUE;
    }
    if (!cell.style.borderRight) {
      cell.style.borderRight = BORDER_VALUE;
    }
  }
  return cell;
};

const checkAndApplyBorderValue = (
  cell: HTMLTableCellElement,
  borderType: 'borderTop' | 'borderBottom' | 'borderLeft' | 'borderRight'
) => {
  if (cell.style[borderType] === 'none') {
    cell.style[borderType] = BORDER_VALUE;
  } else {
    cell.style[borderType] = 'none';
  }
  return cell;
};
